"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "../../../../shared/utils/cn.js";
import { ShinyButton, TextShimmer, useAccountWallet, useB3Config, useModalStore } from "../../../../global-account/react/index.js";
import { thirdwebB3Chain } from "../../../../shared/constants/chains/b3Chain.js";
import { Loader2, ShieldCheck, AlertTriangle, Clock, Wallet } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCreateKycInquiry, useKycStatus, useVerifyKyc, useWalletAuthHeaders } from "../../hooks/useKycStatus.js";
export function KycGate({
  themeColor,
  classes,
  enabled = false,
  onStatusResolved
}) {
  const {
    address
  } = useAccountWallet();
  const {
    partnerId
  } = useB3Config();
  // Gate the status fetch behind explicit user consent so the wallet
  // signature prompt doesn't fire automatically on tab open.
  const [userInitiated, setUserInitiated] = useState(false);
  const {
    getHeaders: preCacheKycHeaders
  } = useWalletAuthHeaders();
  const {
    kycStatus,
    isLoadingKycStatus,
    refetchKycStatus
  } = useKycStatus(enabled && userInitiated);
  const {
    createInquiry,
    isCreatingInquiry
  } = useCreateKycInquiry();
  const {
    verifyKyc,
    isVerifying
  } = useVerifyKyc();
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setB3ModalContentType = useModalStore(state_0 => state_0.setB3ModalContentType);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [personaError, setPersonaError] = useState(null);
  const [personaCancelled, setPersonaCancelled] = useState(false);
  const prevStatusRef = useRef(null);
  // Reset consent gate when wallet changes so the signature prompt isn't
  // skipped for a different (or reconnected) wallet with no cached headers.
  useEffect(() => {
    setUserInitiated(false);
  }, [address]);
  // Notify parent when status resolves
  useEffect(() => {
    if (!kycStatus) return;
    const currentStatus = kycStatus.status;
    if (currentStatus === prevStatusRef.current) return;
    prevStatusRef.current = currentStatus;
    if (!kycStatus.kycRequired || currentStatus === "approved") {
      onStatusResolved(true);
    }
  }, [kycStatus, onStatusResolved]);
  const openPersonaFlow = useCallback(async config => {
    setPersonaOpen(true);
    try {
      // Dynamic import to keep bundle small
      const {
        Client
      } = await import("persona");
      const client = new Client({
        inquiryId: config.inquiryId,
        sessionToken: config.sessionToken,
        environment: config.environment === "production" ? "production" : "sandbox",
        onComplete: async ({
          inquiryId
        }) => {
          // Reopen the modal first so the user lands back in the checkout flow
          setB3ModalOpen(true);
          setPersonaOpen(false);
          if (inquiryId) {
            try {
              const result = await verifyKyc(inquiryId);
              if (result.status === "approved") {
                onStatusResolved(true);
              }
            } catch (err) {
              setPersonaError(err instanceof Error ? err.message : "Verification check failed — please retry.");
            }
          }
          refetchKycStatus();
        },
        onCancel: () => {
          // Reopen the modal so the user can retry or cancel the purchase
          setB3ModalOpen(true);
          setPersonaOpen(false);
          setPersonaCancelled(true);
        },
        onError: error_0 => {
          // Reopen the modal so the user sees the error and can retry
          setB3ModalOpen(true);
          setPersonaOpen(false);
          setPersonaError(error_0?.message || "Verification encountered an error");
        }
      });
      // Close the modal before opening Persona so its overlay is fully
      // interactive — no Radix Dialog backdrop or z-index conflicts.
      // The modal's contentType is preserved in Zustand and restored on reopen.
      setB3ModalOpen(false);
      client.open();
    } catch (error) {
      setPersonaOpen(false);
      setB3ModalOpen(true);
      setPersonaError("Failed to load verification module");
    }
  }, [verifyKyc, onStatusResolved, refetchKycStatus, setB3ModalOpen]);
  const handleStartVerification = useCallback(async () => {
    setPersonaError(null);
    setPersonaCancelled(false);
    try {
      const {
        inquiryId: inquiryId_0,
        sessionToken
      } = await createInquiry();
      openPersonaFlow({
        inquiryId: inquiryId_0,
        sessionToken,
        environment: kycStatus?.config?.environment
      });
    } catch (error_1) {
      setPersonaError(error_1 instanceof Error ? error_1.message : "Failed to start verification");
    }
  }, [createInquiry, kycStatus, openPersonaFlow]);
  const handleConnectWallet = useCallback(() => {
    setB3ModalContentType({
      type: "signInWithB3",
      showBackButton: false,
      chain: thirdwebB3Chain,
      partnerId
    });
    setB3ModalOpen(true);
  }, [setB3ModalContentType, setB3ModalOpen, partnerId]);
  const handleResumeVerification = useCallback(() => {
    if (!kycStatus?.inquiry) return;
    setPersonaError(null);
    setPersonaCancelled(false);
    openPersonaFlow({
      inquiryId: kycStatus.inquiry.inquiryId,
      sessionToken: kycStatus.inquiry.sessionToken,
      environment: kycStatus.config?.environment
    });
  }, [kycStatus, openPersonaFlow]);
  // No wallet connected — prompt to connect wallet (same pattern as crypto tab)
  if (!address) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-kyc-auth flex flex-col items-center gap-4 py-2", classes?.fiatPanel),
      children: [_jsx(ShieldCheck, {
        className: "h-8 w-8 text-gray-400"
      }), _jsxs("div", {
        className: "text-center",
        children: [_jsx("p", {
          className: "text-sm font-medium text-gray-900 dark:text-gray-100",
          children: "Connect wallet to pay with card"
        }), _jsx("p", {
          className: "mt-1 text-xs text-gray-500 dark:text-gray-400",
          children: "A wallet connection is required to complete identity verification."
        })]
      }), _jsx(ShinyButton, {
        accentColor: themeColor || "hsl(var(--as-brand))",
        className: "w-full",
        textClassName: "text-white",
        onClick: handleConnectWallet,
        children: _jsxs("span", {
          className: "flex items-center justify-center gap-2",
          children: [_jsx(Wallet, {
            className: "h-4 w-4"
          }), "Connect Wallet"]
        })
      })]
    });
  }
  // Wallet connected but user hasn't kicked off the KYC check yet
  if (!userInitiated) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-kyc-prompt flex flex-col items-center gap-4 py-2", classes?.fiatPanel),
      children: [_jsx(ShieldCheck, {
        className: "h-8 w-8 text-blue-500"
      }), _jsxs("div", {
        className: "text-center",
        children: [_jsx("p", {
          className: "text-sm font-medium text-gray-900 dark:text-gray-100",
          children: "Identity verification required"
        }), _jsx("p", {
          className: "mt-1 text-xs text-gray-500 dark:text-gray-400",
          children: "Card payments require a one-time identity check. This takes about 2 minutes."
        })]
      }), _jsx(ShinyButton, {
        accentColor: themeColor || "hsl(var(--as-brand))",
        className: "w-full",
        textClassName: "text-white",
        onClick: async () => {
          // Pre-sign in user-gesture context so React Query's queryFn
          // can reuse the cached headers without a second popup.
          // Only enable the query on success — if the user rejects the
          // signature, leave userInitiated=false and stay on this screen.
          try {
            await preCacheKycHeaders();
            setUserInitiated(true);
          } catch {
            // User rejected signature — stay on consent screen
          }
        },
        children: _jsxs("span", {
          className: "flex items-center justify-center gap-2",
          children: [_jsx(ShieldCheck, {
            className: "h-4 w-4"
          }), "Continue to Verify"]
        })
      })]
    });
  }
  // Loading KYC status state
  if (isLoadingKycStatus) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        ease: "easeOut"
      },
      className: cn("anyspend-kyc-loading flex flex-col items-center gap-3 py-6", classes?.fiatPanel),
      children: [_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "text-sm",
        children: "Checking verification status..."
      })]
    });
  }
  // Not required or already approved — render nothing
  if (!kycStatus?.kycRequired || kycStatus.status === "approved") {
    return null;
  }
  // Persona flow is open - show loading
  if (personaOpen) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        ease: "easeOut"
      },
      className: cn("anyspend-kyc-persona flex flex-col items-center gap-3 py-6", classes?.fiatPanel),
      children: [_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "text-sm",
        children: "Identity verification in progress..."
      }), _jsx("p", {
        className: "text-xs text-gray-400 dark:text-gray-500",
        children: "Complete the verification in the popup window"
      })]
    });
  }
  // Needs review or completed (submitted, awaiting Persona decision)
  if (kycStatus.status === "needs_review" || kycStatus.status === "completed") {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-kyc-review flex flex-col items-center gap-3 py-2", classes?.fiatPanel),
      children: [_jsx(Clock, {
        className: "h-8 w-8 text-amber-500"
      }), _jsx("p", {
        className: "text-center text-sm font-medium text-amber-700 dark:text-amber-300",
        children: "Your verification is under review"
      }), _jsx("p", {
        className: "text-center text-xs text-amber-600 dark:text-amber-400",
        children: "This usually takes a few minutes. Please check back shortly."
      }), _jsx("button", {
        onClick: () => refetchKycStatus(),
        className: "mt-1 text-sm font-medium text-amber-700 underline hover:text-amber-800 dark:text-amber-300",
        children: "Check status"
      })]
    });
  }
  // Pending (started before) - offer resume
  if (kycStatus.status === "pending" && kycStatus.inquiry) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-kyc-resume flex flex-col items-center gap-4 py-2", classes?.fiatPanel),
      children: [_jsx(ShieldCheck, {
        className: "h-8 w-8 text-blue-500"
      }), _jsxs("div", {
        className: "text-center",
        children: [_jsx("p", {
          className: "text-sm font-medium text-gray-900 dark:text-gray-100",
          children: "Continue verification"
        }), _jsx("p", {
          className: "mt-1 text-xs text-gray-500 dark:text-gray-400",
          children: "You have an incomplete verification. Resume to continue."
        })]
      }), _jsx(ShinyButton, {
        accentColor: themeColor || "hsl(var(--as-brand))",
        className: "w-full",
        textClassName: "text-white",
        onClick: handleResumeVerification,
        disabled: isCreatingInquiry,
        children: _jsxs("span", {
          className: "flex items-center justify-center gap-2",
          children: [isCreatingInquiry ? _jsx(Loader2, {
            className: "h-4 w-4 animate-spin"
          }) : _jsx(ShieldCheck, {
            className: "h-4 w-4"
          }), "Resume Verification"]
        })
      })]
    });
  }
  // Not verified / declined / expired - show verification prompt
  return _jsxs(motion.div, {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.25,
      ease: "easeOut"
    },
    className: cn("anyspend-kyc-prompt flex flex-col items-center gap-4 py-2", classes?.fiatPanel),
    children: [_jsx(ShieldCheck, {
      className: "h-8 w-8 text-blue-500"
    }), _jsxs("div", {
      className: "text-center",
      children: [_jsx("p", {
        className: "text-sm font-medium text-gray-900 dark:text-gray-100",
        children: "Identity verification required"
      }), _jsx("p", {
        className: "mt-1 text-xs text-gray-500 dark:text-gray-400",
        children: "Card payments require a one-time identity check. This takes about 2 minutes."
      })]
    }), _jsxs(AnimatePresence, {
      initial: false,
      children: [personaError && _jsx(motion.div, {
        initial: {
          opacity: 0,
          height: 0
        },
        animate: {
          opacity: 1,
          height: "auto"
        },
        exit: {
          opacity: 0,
          height: 0
        },
        transition: {
          duration: 0.2,
          ease: "easeOut"
        },
        style: {
          overflow: "hidden"
        },
        className: "w-full rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20",
        children: _jsx("p", {
          className: "text-center text-sm text-red-600 dark:text-red-400",
          children: personaError
        })
      }, "kyc-error"), personaCancelled && _jsx(motion.div, {
        initial: {
          opacity: 0,
          height: 0
        },
        animate: {
          opacity: 1,
          height: "auto"
        },
        exit: {
          opacity: 0,
          height: 0
        },
        transition: {
          duration: 0.2,
          ease: "easeOut"
        },
        style: {
          overflow: "hidden"
        },
        className: "w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20",
        children: _jsx("p", {
          className: "text-center text-sm text-amber-600 dark:text-amber-400",
          children: "Verification cancelled. Click below to try again."
        })
      }, "kyc-cancelled")]
    }), kycStatus.status === "declined" && _jsxs("div", {
      className: "flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20",
      children: [_jsx(AlertTriangle, {
        className: "h-3.5 w-3.5 text-red-500"
      }), _jsx("p", {
        className: "text-xs text-red-600 dark:text-red-400",
        children: "Previous verification was declined. You may try again."
      })]
    }), _jsx(ShinyButton, {
      accentColor: themeColor || "hsl(var(--as-brand))",
      className: "w-full",
      textClassName: "text-white",
      onClick: handleStartVerification,
      disabled: isCreatingInquiry || isVerifying,
      children: _jsxs("span", {
        className: "flex items-center justify-center gap-2",
        children: [isCreatingInquiry ? _jsx(Loader2, {
          className: "h-4 w-4 animate-spin"
        }) : _jsx(ShieldCheck, {
          className: "h-4 w-4"
        }), isCreatingInquiry ? "Starting..." : "Verify Identity"]
      })
    })]
  });
}